import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [token, setToken] = useState(localStorage.getItem("v3d_token"));

  useEffect(() => {
    const check = async () => {
      // Handle return from Emergent-managed Google OAuth: {origin}/dashboard#session_id=...
      const hash = window.location.hash || "";
      if (hash.includes("session_id=")) {
        const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
        // clear the fragment so the token isn't left in the URL / re-processed
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        if (sid) {
          try {
            const { data } = await api.post("/auth/google/session", { session_id: sid, origin: window.location.origin });
            localStorage.setItem("v3d_token", data.access_token);
            setToken(data.access_token);
            const me = await api.get("/auth/me");
            setUser(me.data);
            if (data.linked) {
              toast.success("We linked your Google sign-in to your existing Viral3D account.");
            }
            if (data.is_new && data.starter_project_id) {
              toast.success("Welcome to Viral3D! We set up a starter project for you.");
              window.history.replaceState(null, "", "/studio/" + data.starter_project_id);
              window.location.assign("/studio/" + data.starter_project_id);
            }
            return;
          } catch (e) {
            setUser(false);
            return;
          }
        }
      }
      if (!token) {
        setUser(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        localStorage.removeItem("v3d_token");
        setToken(null);
        setUser(false);
      }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyAuth = async (data) => {
    localStorage.setItem("v3d_token", data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch (e) {}
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) { return null; }
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    await applyAuth(data);
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password, origin: window.location.origin });
    await applyAuth(data);
  };

  const logout = () => {
    localStorage.removeItem("v3d_token");
    setToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
